import type { PlanCategory, PlanDisplayCategory } from '@/client';

/**
 * Broad categories the schedule exposes as user-selectable filters.
 *
 * `awareness` is deliberately absent: event-conditional work belongs with as-needed tasks, while
 * lapsed one-offs belong in History. It is a transport bucket, not one coherent filter choice.
 */
export const FILTERABLE_PLAN_CATEGORIES = [
	'maintenance',
	'warranty',
	'compliance',
] as const satisfies readonly PlanCategory[];
export type FilterablePlanCategory = (typeof FILTERABLE_PLAN_CATEGORIES)[number];

const FILTERABLE_PLAN_CATEGORY_SET: ReadonlySet<string> = new Set(FILTERABLE_PLAN_CATEGORIES);

export const isFilterablePlanCategory = (value: string): value is FilterablePlanCategory =>
	FILTERABLE_PLAN_CATEGORY_SET.has(value);

/** Broad rollups: these answer which part of the plan someone wants to see. */
export const PLAN_CATEGORY_LABEL: Record<FilterablePlanCategory, string> = {
	maintenance: 'Maintenance',
	warranty: 'Warranties',
	compliance: 'Compliance',
};

/** Compact visible names for the finer classification carried on each card. */
export const PLAN_TYPE_LABEL: Record<PlanDisplayCategory, string> = {
	'Manufacturer maintenance': 'Manufacturer maintenance',
	Routine: 'Routine maintenance',
	'Warranty expiry': 'Warranty expiry',
	Compliance: 'Compliance',
	'Code durability': 'Building Code inspection',
};

/** Unknown server values remain legible until the frontend taxonomy catches up. */
export const planCategoryLabel = (value: string): string =>
	isFilterablePlanCategory(value) ? PLAN_CATEGORY_LABEL[value] : value;

/** Unknown server values remain legible until the frontend taxonomy catches up. */
export const planTypeLabel = (value: string): string => PLAN_TYPE_LABEL[value as PlanDisplayCategory] ?? value;
